import { useEffect, useState, useCallback } from 'react';
import { toast } from 'sonner';
import { Trash, ArrowCounterClockwise, Package } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { useT } from '@/i18n/LanguageContext';
import { mfgApi, TYPE_BADGE } from './mfgApi';

export default function DeletedItemsDialog({ open, onClose, onChanged }) {
  const { t } = useT();
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => { mfgApi.archivedItems().then(setItems).catch(() => {}); }, []);
  useEffect(() => { if (open) load(); }, [open, load]);

  const restore = async (it) => {
    setBusy(it.id);
    try { await mfgApi.restoreItem(it.id); toast.success(t('mfg.restored')); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(null); }
  };

  const purge = async (it) => {
    if (!window.confirm(t('mfg.confirmPermanent', { name: it.name }))) return;
    setBusy(it.id);
    try { await mfgApi.deleteItem(it.id, true); toast.success(t('mfg.forceDeleted')); load(); onChanged?.(); }
    catch (e) { toast.error(e?.response?.data?.detail || t('mfg.error')); }
    finally { setBusy(null); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl max-h-[85vh] overflow-y-auto" data-testid="deleted-items-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Trash size={20} className="text-zinc-500" weight="duotone" /> {t('mfg.deletedItems.title')}</DialogTitle>
          <DialogDescription>{t('mfg.deletedItems.desc')}</DialogDescription>
        </DialogHeader>
        {items.length === 0 ? (
          <div className="py-12 text-center" data-testid="deleted-items-empty">
            <Package size={40} weight="duotone" className="text-zinc-300 mx-auto mb-2" />
            <p className="text-sm text-zinc-500">{t('mfg.deletedItems.empty')}</p>
          </div>
        ) : (
          <div className="rounded-lg border border-zinc-200 overflow-hidden">
            <table className="w-full text-sm" data-testid="deleted-items-table">
              <thead><tr className="bg-zinc-100 text-zinc-700 text-xs border-b border-zinc-200">
                <th className="text-left font-semibold px-3 py-2">{t('mfg.item.sku')}</th>
                <th className="text-left font-semibold px-3 py-2">{t('mfg.item.name')}</th>
                <th className="text-right font-semibold px-3 py-2"></th>
              </tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-zinc-200" data-testid={`deleted-row-${it.id}`}>
                    <td className="px-3 py-2 font-mono text-zinc-600">{it.sku}</td>
                    <td className="px-3 py-2">
                      <span className="font-medium text-zinc-800">{it.name}</span>
                      <Badge variant="outline" className={`ml-2 ${TYPE_BADGE[it.type]}`}>{t(`mfg.type.${it.type}`)}</Badge>
                    </td>
                    <td className="px-3 py-2"><div className="flex justify-end gap-1">
                      <Button size="sm" variant="outline" disabled={busy === it.id} onClick={() => restore(it)} data-testid={`restore-${it.id}`} className="h-8 px-2 text-emerald-700 border-emerald-200 hover:bg-emerald-50">
                        <ArrowCounterClockwise size={15} className="mr-1" /> {t('mfg.restore')}
                      </Button>
                      <Button size="sm" variant="ghost" disabled={busy === it.id} onClick={() => purge(it)} data-testid={`purge-${it.id}`} className="h-8 px-2 text-red-600 hover:bg-red-50" title={t('mfg.deletedItems.permanent')}>
                        <Trash size={15} />
                      </Button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
